import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import { Loader2, Calendar } from "lucide-react";
import { format } from "date-fns";
import StoreLocatorHeader from "./components/StoreLocatorHeader";
import StoreLocatorTreeView from "./components/StoreLocatorTreeView";
import StoreLocatorDetailsPanel from "./components/StoreLocatorDetailsPanel";
import StoreLocatorPostsSection from "./components/StoreLocatorPostsSection";
import StoreLocatorFooter from "./components/StoreLocatorFooter";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

interface Location {
  id: string;
  name: string;
  address: string;
  formatted_address: string;
  location_city?: string;
  location_short_address?: string;
  primary_phone?: string;
  website_uri?: string;
  latitude?: number;
  longitude?: number;
  regular_hours?: any;
  auto_detected_services?: string[];
  photos?: any[];
  rating?: number;
  review_count?: number;
  primary_category?: string;
  additional_categories?: any[];
  description?: string;
  distance?: number;
  media_items?: any;
}

interface Post {
  post_id: string;
  google_post_id: string;
  status: string;
  published_at: string;
  metadata: any;
  post_content: {
    id: string;
    summary: string;
    media_urls?: string[];
    type?: string;
    call_to_action?: any;
    event_info?: any;
    offer_info?: any;
  };
}

interface AgencyBranding {
  brandName: string;
  logoUrl?: string;
  primaryColor?: string;
  websiteUrl?: string;
}

interface StoreLocatorData {
  subdomain: string;
  theme_config: any;
  seo_config: any;
  workspace: {
    id: string;
    name: string;
    brand_name: string;
    logo_url: string;
    business_description: string;
    website: string;
    facebook_url: string;
    instagram_url: string;
    twitter_url: string;
    linkedin_url: string;
    youtube_url: string;
    agency_id?: string;
  };
  locations: Location[];
  store_settings?: any;
  agencyBranding?: AgencyBranding;
}

interface StoreLocatorPageProps {
  locationSlug?: string;
  // Edit mode props (for Microsites Manager)
  editMode?: boolean;
  workspaceId?: string;
  // SSR mode props (for server-side rendering)
  ssrData?: StoreLocatorData;
  subdomain?: string;
  ssrSelectedLocationPosts?: Post[] | null;
  ssrSelectedLocationReviews?: any[] | null;
}

const StoreLocatorPage = ({
  locationSlug,
  editMode = false,
  workspaceId,
  ssrData,
  subdomain: subdomainProp,
  ssrSelectedLocationPosts: ssrPostsProp = null,
  ssrSelectedLocationReviews: ssrReviewsProp = null
}: StoreLocatorPageProps) => {
  const params = useParams<{ subdomain: string }>();
  const navigate = useNavigate();

  // Extract subdomain from props OR URL params OR hostname
  // In edit mode, subdomain is passed as prop from MicrositesPage
  // In public mode, subdomain comes from URL params or hostname
  const getSubdomain = () => {
    // First check if passed as prop (edit mode)
    if (subdomainProp) {
      console.log('[StoreLocatorPage] Using subdomain from prop:', subdomainProp);
      return subdomainProp;
    }

    // Then check URL params (for /subdomain/:subdomain route)
    if (params.subdomain) {
      console.log('[StoreLocatorPage] Using subdomain from URL params:', params.subdomain);
      return params.subdomain;
    }

    // Finally check hostname (for subdomain.localhost or subdomain.my-domain.com)
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');

      if (parts.length >= 2 && parts[0] !== 'localhost' && parts[0] !== 'www') {
        console.log('[StoreLocatorPage] Using subdomain from hostname:', parts[0]);
        return parts[0];
      }
    }

    return null;
  };

  const subdomain = getSubdomain();

  // Helper function to generate slug from text
  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Helper function to find location by slug
  const findLocationBySlug = (locations: Location[], slug: string): Location | null => {
    return locations.find(loc => generateSlug(loc.name) === slug) || null;
  };

  // IMPORTANT: Initialize state based on whether SSR data is available
  // During SSR, we have data immediately, so loading should be false
  // During CSR (client-side), we need to fetch data, so loading starts as true
  const [storeData, setStoreData] = useState<StoreLocatorData | null>(ssrData || null);

  // Initialize selectedLocation if we have SSR data + locationSlug
  const initialSelectedLocation =
    ssrData && locationSlug && ssrData.locations
      ? findLocationBySlug(ssrData.locations, locationSlug)
      : null;

  // Initialize expandedCities if we have a pre-selected location (for SSR)
  const getInitialExpandedCities = (): Set<string> => {
    if (!initialSelectedLocation) return new Set();
    // Extract city from location for initial expansion
    const city = initialSelectedLocation.location_city || 'Other Cities';
    return new Set([city]);
  };

  const [selectedLocation, setSelectedLocation] = useState<Location | null>(initialSelectedLocation);
  const [loading, setLoading] = useState(!ssrData); // false if ssrData exists, true otherwise
  const [error, setError] = useState<string | null>(null);
  const [expandedCities, setExpandedCities] = useState<Set<string>>(getInitialExpandedCities());
  const [selectedCity, setSelectedCity] = useState<string>("all");
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [postModalOpen, setPostModalOpen] = useState(false);

  // Initialize SSR posts/reviews from props (for server-side rendering)
  // or from null (for client-side rendering where they'll be fetched)
  const [ssrSelectedLocationPosts, setSsrSelectedLocationPosts] = useState<Post[] | null>(ssrPostsProp);
  const [ssrSelectedLocationReviews, setSsrSelectedLocationReviews] = useState<any[] | null>(ssrReviewsProp);

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // If SSR data is provided, we already initialized state with it, so skip fetching
    if (ssrData) {
      console.log('[StoreLocatorPage] Using SSR data from props');
      return;
    }

    // Check if we have SSR pre-fetched data from window (for client-side hydration)
    if (typeof window !== 'undefined' && (window as any).__SSR_DATA__) {
      const windowSsrData = (window as any).__SSR_DATA__;

      // Check if this is store locator data
      if (windowSsrData.subdomain_type === 'store_locator' && windowSsrData.storeLocatorData) {
        console.log('[StoreLocatorPage] Using SSR pre-fetched data from window');
        setStoreData(windowSsrData.storeLocatorData);

        // If there are pre-fetched posts for selected location, save them
        if (windowSsrData.selectedLocationPosts && windowSsrData.selectedLocationPosts.length > 0) {
          console.log('[StoreLocatorPage] Found SSR posts for selected location');
          setSsrSelectedLocationPosts(windowSsrData.selectedLocationPosts);
        }

        setLoading(false);

        // Clear SSR data after using it
        delete (window as any).__SSR_DATA__;
        return;
      }
    }

    // Otherwise fetch data from database
    fetchStoreLocatorData();
  }, [subdomain, ssrData]);

  useEffect(() => {
    if (locationSlug && storeData?.locations) {
      const location = findLocationBySlug(storeData.locations, locationSlug);
      if (location) {
        setSelectedLocation(location);
        const city = getCityFromLocation(location);
        if (city) {
          setExpandedCities(new Set([city]));
        }
      }
    }
    // Don't auto-select first location - let user choose from the list
  }, [locationSlug, storeData]);

  const fetchStoreLocatorData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[StoreLocatorPage] Fetching store locator data...');
      console.log('[StoreLocatorPage] Edit mode:', editMode);
      console.log('[StoreLocatorPage] Subdomain:', subdomain);
      console.log('[StoreLocatorPage] Workspace ID:', workspaceId);

      // In edit mode, use workspaceId prop to fetch data
      // In public mode, use subdomain to fetch data
      let subdomainConfig: any;
      let finalWorkspaceId: string;

      if (editMode && workspaceId) {
        // EDIT MODE: Fetch by workspace_id directly
        console.log('[StoreLocatorPage] Edit mode: Fetching by workspace_id');

        // Fetch workspace data
        const { data: workspace, error: workspaceError } = await supabase
          .from("workspaces")
          .select("*")
          .eq("id", workspaceId)
          .single();

        if (workspaceError || !workspace) {
          throw new Error("Workspace not found");
        }

        // Fetch subdomain config (might not exist yet)
        const { data: configData } = await supabase
          .from("subdomain_configs")
          .select("*")
          .eq("workspace_id", workspaceId)
          .eq("subdomain_type", "store_locator")
          .maybeSingle();

        subdomainConfig = {
          subdomain: configData?.subdomain || "",
          theme_config: configData?.theme_config || {
            primaryColor: "#0066cc",
            secondaryColor: "#f0f0f0",
            fontFamily: "Inter"
          },
          seo_config: configData?.seo_config || {
            title: "",
            description: "",
            keywords: []
          },
          workspace_id: workspaceId,
          workspace: workspace
        };

        finalWorkspaceId = workspaceId;
      } else {
        // PUBLIC MODE: Fetch by subdomain
        console.log('[StoreLocatorPage] Public mode: Fetching by subdomain');
        const { data, error: configError } = await supabase
          .from("subdomain_configs")
          .select(`
            *,
            workspace:workspaces!workspace_id(*)
          `)
          .eq("subdomain", subdomain)
          .eq("subdomain_type", "store_locator")
          .eq("is_active", true)
          .single();

        if (configError || !data) {
          console.error('[StoreLocatorPage] Error fetching config:', configError);
          throw new Error("Store locator not found");
        }

        subdomainConfig = data;
        finalWorkspaceId = data.workspace_id;
      }

      console.log('[StoreLocatorPage] Subdomain config fetched:', subdomainConfig);

      const { data: locations, error: locationsError } = await supabase
        .from("gmb_locations")
        .select("*")
        .eq("workspace_id", finalWorkspaceId)
        .in("status", ["connected", "active"])
        .order("name");

      console.log('Locations fetched:', locations?.length || 0);
      console.log('Locations error:', locationsError);

      if (locationsError) {
        console.error("Error fetching locations:", locationsError);
      }

      const { data: storeSettings, error: settingsError } = await supabase
        .from("subdomain_store_settings")
        .select("*")
        .eq("workspace_id", finalWorkspaceId)
        .maybeSingle();

      if (settingsError) {
        console.error("Error fetching store settings:", settingsError);
      }

      // Fetch agency branding if workspace belongs to an agency
      let agencyBranding: AgencyBranding | undefined;
      const workspaceData = subdomainConfig.workspace;
      if (workspaceData?.agency_id) {
        console.log('[StoreLocatorPage] Fetching agency branding for agency_id:', workspaceData.agency_id);
        const { data: agency, error: agencyError } = await supabase
          .from("agencies")
          .select("brand_name, logo_url, primary_color, website_url")
          .eq("id", workspaceData.agency_id)
          .single();

        if (agencyError) {
          console.error('[StoreLocatorPage] Error fetching agency:', agencyError);
        } else if (agency) {
          agencyBranding = {
            brandName: agency.brand_name,
            logoUrl: agency.logo_url,
            primaryColor: agency.primary_color,
            websiteUrl: agency.website_url,
          };
          console.log('[StoreLocatorPage] Agency branding loaded:', agencyBranding);
        }
      }

      // Build theme config, defaulting to agency colors if not customized
      const baseTheme = subdomainConfig.theme_config || { primaryColor: "#0066cc", secondaryColor: "#f0f0f0", fontFamily: "Inter" };
      const themeWithAgencyDefaults = {
        ...baseTheme,
        // Use agency colors as defaults if theme doesn't have custom colors
        primaryColor: baseTheme.primaryColor || agencyBranding?.primaryColor || "#0066cc",
      };

      const storeLocatorData: StoreLocatorData = {
        subdomain: subdomainConfig.subdomain || "",
        theme_config: themeWithAgencyDefaults,
        seo_config: subdomainConfig.seo_config,
        workspace: subdomainConfig.workspace,
        locations: locations || [],
        store_settings: storeSettings,
        agencyBranding,
      };

      setStoreData(storeLocatorData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const extractCityFromAddress = (address: string): string | null => {
    const parts = address.split(',').map(p => p.trim());
    const indianStates = [
      'Maharashtra', 'Gujarat', 'Karnataka', 'Tamil Nadu', 'Kerala',
      'Andhra Pradesh', 'Telangana', 'West Bengal', 'Rajasthan', 'Uttar Pradesh',
      'Madhya Pradesh', 'Punjab', 'Haryana', 'Delhi', 'Goa', 'Bihar',
      'Odisha', 'Jharkhand', 'Chhattisgarh', 'Assam', 'Himachal Pradesh',
      'Uttarakhand', 'Jammu and Kashmir', 'Meghalaya', 'Manipur', 'Nagaland',
      'Tripura', 'Mizoram', 'Arunachal Pradesh', 'Sikkim'
    ];

    let stateIndex = -1;
    for (let i = parts.length - 1; i >= 0; i--) {
      if (indianStates.some(state => parts[i].includes(state))) {
        stateIndex = i;
        break;
      }
    }

    if (stateIndex > 0) {
      const potentialCity = parts[stateIndex - 1];
      if (potentialCity && !/^\d+$/.test(potentialCity)) {
        return potentialCity;
      }
    }

    return null;
  };

  const getCityFromLocation = (location: Location): string => {
    if (location.formatted_address) {
      try {
        const address = typeof location.formatted_address === 'string'
          ? JSON.parse(location.formatted_address)
          : location.formatted_address;
        return address.locality || address.administrativeArea || 'Other Cities';
      } catch (e) {
        if (location.address) {
          const extractedCity = extractCityFromAddress(location.address);
          if (extractedCity) return extractedCity;
        }
        return location.location_city || 'Other Cities';
      }
    } else if (location.address) {
      const extractedCity = extractCityFromAddress(location.address);
      if (extractedCity) return extractedCity;
    }
    return location.location_city || 'Other Cities';
  };

  const getLocationsByCity = () => {
    if (!storeData?.locations) return {};

    // Filter locations by search query first
    let filteredLocations = storeData.locations;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filteredLocations = storeData.locations.filter(location => {
        const city = getCityFromLocation(location).toLowerCase();
        const address = formatAddress(location).toLowerCase();
        const name = location.name.toLowerCase();

        return city.includes(query) ||
               address.includes(query) ||
               name.includes(query);
      });
    }

    const allLocations = filteredLocations.reduce((acc: any, location) => {
      const city = getCityFromLocation(location);
      if (!acc[city]) {
        acc[city] = [];
      }
      acc[city].push(location);
      return acc;
    }, {});

    if (selectedCity !== "all") {
      const filtered: any = {};
      if (allLocations[selectedCity]) {
        filtered[selectedCity] = allLocations[selectedCity];
      }
      return filtered;
    }

    return allLocations;
  };

  const getAllCities = () => {
    if (!storeData?.locations) return [];

    const cities = new Set<string>();
    storeData.locations.forEach(location => {
      const city = getCityFromLocation(location);
      cities.add(city);
    });

    return Array.from(cities).sort();
  };

  const toggleCity = (city: string) => {
    const newExpanded = new Set(expandedCities);
    if (newExpanded.has(city)) {
      newExpanded.delete(city);
    } else {
      newExpanded.add(city);
    }
    setExpandedCities(newExpanded);
  };

  const formatAddress = (location: Location) => {
    if (location.formatted_address) {
      try {
        const address = typeof location.formatted_address === 'string'
          ? JSON.parse(location.formatted_address)
          : location.formatted_address;

        const parts = [];
        if (address.addressLines?.length > 0) {
          parts.push(...address.addressLines);
        }
        if (address.locality) parts.push(address.locality);
        if (address.administrativeArea) parts.push(address.administrativeArea);
        if (address.postalCode) parts.push(address.postalCode);
        if (address.regionCode) parts.push(address.regionCode);

        return parts.join(', ');
      } catch (e) {
        return location.address || location.location_short_address || '';
      }
    }
    return location.address || location.location_short_address || '';
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatDate = (dateString: string) => {
    // Use date-fns rather than toLocaleDateString: the latter is
    // locale/timezone-dependent, which produces different strings on
    // the SSR server (UTC, en-US) vs the client browser (visitor's
    // locale + timezone) and triggers React hydration mismatches on
    // this microsite (Sentry: microsite-hydration-mismatch).
    return format(new Date(dateString), 'dd MMM yy');
  };

  const handleLocationSelect = (location: Location) => {
    console.log('[StoreLocatorPage] Location selected:', location.name);

    // Close sidebar on mobile when location is selected
    setSidebarOpen(false);

    // In edit mode, don't navigate - just update selected location
    if (editMode) {
      console.log('[StoreLocatorPage] Edit mode: Preventing navigation, just selecting location');
      setSelectedLocation(location);
      return;
    }

    // In public mode, navigate to location detail page
    const slug = generateSlug(location.name);
    const isOnSubdomain = typeof window !== 'undefined' &&
      window.location.hostname.split('.').length >= 2 &&
      window.location.hostname.split('.')[0] !== 'localhost' &&
      window.location.hostname.split('.')[0] !== 'www';

    const basePath = isOnSubdomain ? '' : `/subdomain/${subdomain}`;
    console.log('[StoreLocatorPage] Navigating to:', `${basePath}/${slug}`);
    navigate(`${basePath}/${slug}`);
    setSelectedLocation(location);
  };

  const handleCityClick = (city: string) => {
    setSelectedCity(city);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openPostModal = (post: Post) => {
    setSelectedPost(post);
    setPostModalOpen(true);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    // Reset city filter when searching
    if (query.trim()) {
      setSelectedCity("all");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (error || !storeData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Store Locator Not Found
          </h1>
          <p className="text-gray-600">
            The store locator you're looking for doesn't exist.
          </p>
        </div>
      </div>
    );
  }

  const { theme_config, seo_config, workspace } = storeData;
  const locationsByCity = getLocationsByCity();
  const allCities = getAllCities();

  const themeStyles = {
    "--primary-color": theme_config?.primaryColor || "#0066cc",
    "--secondary-color": theme_config?.secondaryColor || "#f0f0f0",
    "--font-family": theme_config?.fontFamily || "Inter",
    fontFamily: theme_config?.fontFamily || "Inter",
  } as React.CSSProperties;

  return (
    <>
      <Helmet>
        <title>{seo_config?.title || `${workspace.brand_name || workspace.name} - Store Locator`}</title>
        <meta
          name="description"
          content={seo_config?.description || `Find ${workspace.brand_name || workspace.name} locations near you`}
        />
        <meta name="keywords" content={seo_config?.keywords?.join(", ") || ""} />
        <link rel="canonical" href={`https://${subdomain}.fly-social.com`} />
      </Helmet>

      <div style={themeStyles} className="min-h-screen bg-gray-50 flex flex-col">
        <StoreLocatorHeader
          businessName={workspace.brand_name || workspace.name}
          logoUrl={workspace.logo_url}
          theme={theme_config}
          totalLocations={storeData.locations.length}
          websiteUrl={workspace.website}
          onSearch={handleSearch}
          searchQuery={searchQuery}
          onMenuClick={() => setSidebarOpen(true)}
          showMenuButton={true}
        />

        {/* Mobile Sidebar with Tree View */}
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent side="left" className="w-[85vw] sm:w-[400px] p-0">
            <SheetHeader className="px-4 pt-4 pb-2">
              <SheetTitle>Select Location</SheetTitle>
            </SheetHeader>
            <div className="px-4 pb-4 h-[calc(100vh-80px)] overflow-hidden">
              <StoreLocatorTreeView
                locations={storeData.locations}
                locationsByCity={locationsByCity}
                selectedLocation={selectedLocation}
                selectedCity={selectedCity}
                expandedCities={expandedCities}
                allCities={allCities}
                theme={theme_config}
                subdomain={subdomain || ''}
                onLocationSelect={handleLocationSelect}
                onCityChange={setSelectedCity}
                onCityToggle={toggleCity}
                formatAddress={formatAddress}
                onSearch={handleSearch}
                searchQuery={searchQuery}
                showSearchBar={true}
              />
            </div>
          </SheetContent>
        </Sheet>

        <div className="flex-1 max-w-7xl mx-auto w-full px-4 py-4 md:py-6 flex flex-col">
          <div className="flex-1 flex flex-col lg:grid lg:grid-cols-3 gap-4 md:gap-6">
            {/* Details Panel - First on mobile, spans 2 cols on desktop */}
            <div className="order-1 lg:order-2 lg:col-span-2">
              <StoreLocatorDetailsPanel
                location={selectedLocation}
                theme={theme_config}
                workspace={workspace}
                formatAddress={formatAddress}
                formatPhoneNumber={formatPhoneNumber}
                ssrReviewsData={ssrSelectedLocationReviews || undefined}
              >
                {selectedLocation && (
                  <StoreLocatorPostsSection
                    locationId={selectedLocation.id}
                    theme={theme_config}
                    onPostClick={openPostModal}
                    ssrPostsData={ssrSelectedLocationPosts || undefined}
                  />
                )}
              </StoreLocatorDetailsPanel>
            </div>

            {/* Tree View - Hidden on mobile (in sidebar), visible on desktop */}
            <div className="hidden lg:block lg:order-1 lg:col-span-1">
              <StoreLocatorTreeView
                locations={storeData.locations}
                locationsByCity={locationsByCity}
                selectedLocation={selectedLocation}
                selectedCity={selectedCity}
                expandedCities={expandedCities}
                allCities={allCities}
                theme={theme_config}
                subdomain={subdomain || ''}
                onLocationSelect={handleLocationSelect}
                onCityChange={setSelectedCity}
                onCityToggle={toggleCity}
                formatAddress={formatAddress}
                onSearch={handleSearch}
                searchQuery={searchQuery}
                showSearchBar={false}
              />
            </div>
          </div>
        </div>

        <StoreLocatorFooter
          cities={allCities}
          selectedCity={selectedCity}
          brandName={workspace.brand_name || workspace.name}
          theme={theme_config}
          subdomain={subdomain || ''}
          onCityClick={handleCityClick}
          agencyBranding={storeData.agencyBranding}
        />

        <Dialog open={postModalOpen} onOpenChange={setPostModalOpen}>
          <DialogContent className="max-w-[95vw] md:max-w-2xl max-h-[90vh] md:max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-base md:text-lg">
                {`Post from ${selectedLocation?.name}`}
              </DialogTitle>
            </DialogHeader>
            {selectedPost && (
              <div className="space-y-3 md:space-y-4">
                {selectedPost.post_content?.media_urls && selectedPost.post_content.media_urls.length > 0 && (
                  <div className="w-full">
                    {/* The post text follows immediately below in this dialog,
                        so the artwork carries no extra meaning and takes a
                        deliberate empty alt. */}
                    <img
                      src={selectedPost.post_content.media_urls[0]}
                      alt=""
                      className="w-full h-auto max-h-64 md:max-h-96 object-contain rounded-lg"
                      onError={(e) => {
                        const imgUrl = selectedPost.post_content.media_urls?.[0];
                        if (imgUrl && imgUrl.includes('googleusercontent.com')) {
                          const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(imgUrl)}&w=800`;
                          e.currentTarget.src = proxyUrl;
                        } else {
                          const container = e.currentTarget.parentElement;
                          if (container) container.style.display = 'none';
                        }
                      }}
                    />
                  </div>
                )}

                {selectedPost.post_content?.summary && (
                  <div className="prose max-w-none">
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed text-sm md:text-lg">
                      {selectedPost.post_content.summary}
                    </p>
                  </div>
                )}

                <div className="flex flex-col md:flex-row items-start md:items-center justify-between pt-3 md:pt-4 border-t border-gray-200 gap-3">
                  <div className="flex items-center gap-2 text-xs md:text-sm text-gray-500">
                    <Calendar className="h-3 w-3 md:h-4 md:w-4" />
                    Published on {formatDate(selectedPost.published_at)}
                  </div>

                  {selectedPost.post_content?.call_to_action?.url && selectedPost.post_content?.call_to_action?.actionType && (
                    <a
                      href={selectedPost.post_content.call_to_action.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full md:w-auto text-center px-4 py-2 rounded-lg font-medium text-white hover:opacity-90 transition-opacity text-sm md:text-base"
                      style={{ backgroundColor: theme_config?.primaryColor || "#0066cc" }}
                    >
                      {selectedPost.post_content.call_to_action.actionType === 'LEARN_MORE' ? 'Learn More' :
                       selectedPost.post_content.call_to_action.actionType === 'BOOK' ? 'Book Now' :
                       selectedPost.post_content.call_to_action.actionType === 'ORDER' ? 'Order Online' :
                       selectedPost.post_content.call_to_action.actionType === 'BUY' ? 'Buy Now' :
                       selectedPost.post_content.call_to_action.actionType === 'SIGN_UP' ? 'Sign Up' :
                       'View More'}
                    </a>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default StoreLocatorPage;
